import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Shield } from 'lucide-react';

export const PrivacyPolicy = () => {
  return (
    <div data-testid="privacy-policy-page">
      <Helmet>
        <title>Privacy Policy | A-Quality Pool Service</title>
        <meta name="description" content="A-Quality Pool Service privacy policy. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://a-qualitypools.net/privacy" />
      </Helmet>
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Shield className="h-5 w-5 text-sky-400 mr-2" />
              <span className="text-white font-medium">Your Privacy Matters</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-slate-300">Last Updated: April 15, 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 prose prose-slate prose-lg max-w-none">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              A-Quality Pool Service ("we," "us," or "our") is committed to protecting the privacy of our customers and website visitors. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. By accessing or using our website, you agree to the terms of this Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">Personal Information</h3>
            <p className="text-slate-600 mb-4">We may collect the following personal information when you voluntarily provide it to us:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Mailing or service address</li>
              <li>Information about your pool and service needs</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">Automatically Collected Information</h3>
            <p className="text-slate-600 mb-4">When you visit our website, we may automatically collect certain information, including:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on our site</li>
              <li>Referring website addresses</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>To respond to your inquiries and provide customer support</li>
              <li>To schedule and deliver pool services</li>
              <li>To send you service reminders, updates, and promotional materials (with your consent)</li>
              <li>To improve our website and services</li>
              <li>To comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Information Sharing</h2>
            <p className="text-slate-600 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Service Providers:</strong> We may share information with trusted third-party vendors who assist us in operating our website and conducting our business, subject to confidentiality agreements.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect the rights, property, or safety of A-Quality Pool Service, our customers, or others.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement reasonable administrative, technical, and physical security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Cookies and Tracking Technologies</h2>
            <p className="text-slate-600 mb-4">
              Our website may use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small data files stored on your device. You can control cookie preferences through your browser settings; however, disabling cookies may affect the functionality of our website.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. Third-Party Links</h2>
            <p className="text-slate-600 mb-4">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party websites you visit.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-600 mb-4">
              Our website and services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have inadvertently collected such information, we will take steps to delete it promptly.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Your Rights</h2>
            <p className="text-slate-600 mb-4">You may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Request access to the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of receiving promotional communications</li>
            </ul>
            <p className="text-slate-600 mb-4">
              To exercise any of these rights, please contact us using the information provided below.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">10. Changes to This Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">11. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-4">
              <p className="text-slate-700 font-semibold">A-Quality Pool Service</p>
              <p className="text-slate-600">A Cody Pools Company</p>
              <p className="text-slate-600">Phone: 813-453-5988</p>
              <p className="text-slate-600">Email: info@a-qualitypools.net</p>
              <p className="text-slate-600">License # CPC1460475</p>
            </div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
